/* Optional props carry ES6 defaults rather than a defaultProps object, which is
   the pattern the rest of the codebase uses and what React 18 wants. */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import {
  Bar,
  BarChart,
  Cell,
  Label,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

/**
 * Vertical bar chart, client-only, animating in the first time it is scrolled
 * into view.
 *
 * Lifted verbatim out of `src/pages/schools/annual-reports/2025.tsx`, where it
 * was the local `ChartPlaceholder`, so the UK schools page can draw the same
 * age histogram. Behaviour is unchanged except for `percentSuffix`, which was
 * a file-level `PCT_SUFFIX_FI = " %"` constant. Finnish typography puts a
 * space before the percent sign and English does not, so it is now a prop —
 * and it still defaults to `" %"`, which is what the report renders.
 *
 * Two things here are deliberate rather than incidental:
 *
 * - It renders nothing until `mounted`. Recharts measures the DOM, so a
 *   server-rendered pass would bake in a zero-width chart that never resizes.
 *   The placeholder keeps the same height so the page does not jump.
 * - The IntersectionObserver fires once at a 0.5 threshold and is never reset,
 *   so the bars grow exactly once. Re-animating on every scroll past reads as
 *   a glitch, not as motion.
 */

type Datum = Record<string, unknown> & { fill?: string }

type Series = {
  dataKey: string
  label: string
  fill: string
}

type YearBarChartProps = {
  chartData: Datum[]
  /** Light text and axes for a dark section background. */
  dark?: boolean
  xDataKey?: string
  valueKey?: string
  tooltipLabel?: string
  tooltipValueLabel?: string
  /** `"%"` switches on percentage formatting; anything else is appended as-is. */
  valueSuffix?: string
  /** What to print after a percentage. `" %"` for Finnish, `"%"` for English. */
  percentSuffix?: string
  /** Grouped bars. When omitted, one bar per row coloured from its own `fill`. */
  series?: Series[] | null
  xAxisLabel?: string
  yAxisLabel?: string
}

export default function YearBarChart({
  chartData,
  dark = false,
  xDataKey = "year",
  valueKey = "cases",
  tooltipLabel = "Year",
  tooltipValueLabel = "Cases",
  valueSuffix = "",
  percentSuffix = " %",
  series = null,
  xAxisLabel = "",
  yAxisLabel = "",
}: YearBarChartProps) {
  const [mounted, setMounted] = useState(false)
  const [chartInView, setChartInView] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted || !containerRef.current) return undefined
    const el = containerRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setChartInView(true)
      },
      { threshold: 0.5, rootMargin: "0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [mounted])

  if (!mounted) {
    return <div className="h-full min-h-[20rem] w-full" aria-hidden />
  }

  const tickFill = dark ? "#e4e4e7" : "#27272a"
  const axisStroke = dark ? "#71717a" : "#52525b"
  const axisLabelFill = dark ? "#a1a1aa" : "#71717a"
  const tickStyle = { fontSize: 14, fontWeight: 600 }
  const suffix = valueSuffix === "%" ? percentSuffix : valueSuffix

  // Axis ticks stay whole numbers; the tooltip is where the decimal belongs.
  const formatValue = (v: number | string) => {
    if (typeof v !== "number") return String(v) + suffix
    if (valueSuffix === "%") return v.toFixed(0) + suffix
    if (v >= 1000) return `${v / 1000}k${suffix}`
    return v + suffix
  }

  const formatTooltipValue = (
    v: number | string | Array<number | string>,
  ): string => {
    if (Array.isArray(v)) {
      return v.map((item) => formatTooltipValue(item)).join(" - ")
    }
    return typeof v === "number" ? v.toFixed(1) + suffix : String(v) + suffix
  }

  // Remount the <Bar> whenever the values change.
  //
  // An animated Recharts 2.x <Bar> keeps its computed rectangles in internal
  // state and does not recompute them when `data` is swapped for an array of
  // the same shape — the bars keep their old heights. That is why the year
  // picker on the annual report has never actually changed the chart: the
  // buttons highlight, the bars do not move. Keying on the values gives the
  // Bar a fresh mount, which re-runs the grow animation for the new year.
  //
  // The key goes on the <Bar> and not on <BarChart>: remounting the whole
  // chart inside ResponsiveContainer leaves the bars stuck at zero height,
  // because the fresh chart animates before the container has handed it a
  // measured size.
  //
  // Derived here rather than taken as a prop so every caller gets the correct
  // behaviour without having to know about the bug.
  const signatureKeys = series ? series.map((s) => s.dataKey) : [valueKey]
  const dataSignature = chartData
    .map((row) => signatureKeys.map((k) => String(row[k] ?? "")).join("/"))
    .join(",")

  return (
    <div ref={containerRef} className="h-full min-h-[20rem] w-full">
      {chartInView && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="h-full w-full"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 16, right: 16, left: 8, bottom: 20 }}
            >
              <XAxis
                dataKey={xDataKey}
                tick={{ fill: tickFill, ...tickStyle }}
                axisLine={{ stroke: axisStroke, strokeWidth: 2 }}
                tickLine={{ stroke: axisStroke, strokeWidth: 1 }}
              >
                {xAxisLabel ? (
                  <Label
                    value={xAxisLabel}
                    position="insideBottom"
                    offset={-2}
                    dy={4}
                    style={{
                      fill: axisLabelFill,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  />
                ) : null}
              </XAxis>
              <YAxis
                tick={{ fill: tickFill, ...tickStyle }}
                axisLine={{ stroke: axisStroke, strokeWidth: 2 }}
                tickLine={{ stroke: axisStroke, strokeWidth: 1 }}
                tickFormatter={formatValue}
              >
                {yAxisLabel ? (
                  <Label
                    value={yAxisLabel}
                    angle={-90}
                    position="insideLeft"
                    offset={6}
                    style={{
                      fill: axisLabelFill,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  />
                ) : null}
              </YAxis>
              <Tooltip
                cursor={false}
                contentStyle={{
                  backgroundColor: dark ? "#3f3f46" : "#27272a",
                  border: "none",
                  borderRadius: "8px",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                itemStyle={{ color: "#ffffff" }}
                labelStyle={{ color: "#e4e4e7" }}
                formatter={(value, name) => {
                  const label = series ? name : tooltipValueLabel
                  return [formatTooltipValue(value), label]
                }}
                labelFormatter={(label) => `${tooltipLabel} ${label}`}
              />
              {series && (
                <Legend
                  wrapperStyle={{
                    color: tickFill,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                />
              )}
              {series ? (
                series.map((s) => (
                  <Bar
                    key={s.dataKey}
                    dataKey={s.dataKey}
                    name={s.label}
                    fill={s.fill}
                    activeBar={{ fill: "#72c9ac" }}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive
                    animationBegin={0}
                    animationDuration={800}
                  />
                ))
              ) : (
                <Bar
                  key={dataSignature}
                  dataKey={valueKey}
                  activeBar={{ fill: "#72c9ac" }}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive
                  animationBegin={0}
                  animationDuration={800}
                >
                  {chartData.map((entry, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </div>
  )
}
